import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { deskApi } from "@/lib/deskApi";
import { QueryError, retryTransient } from "@/lib/queryState";
import { Badge, Panel, SegmentedControl, Skeleton, StatCell, fmt } from "@/sentinel";

const usd = (v) => (v == null ? "—" : fmt(v, "usd", { signed: true }));
const usdAbs = (v) => (v == null ? "—" : fmt(Math.abs(v), "usd"));

/**
 * "Cost of saying no" (item 14): what the top-ranked spread would have made or lost at every run the
 * system refused, from GET /v1/scoring/cost-of-no. It is a record, not a verdict: the figures are
 * shown with a range computed across sessions, with the number of sessions still needed before the
 * window could show an edge either way, and grouped by why the system said no. Hidden entirely when
 * the endpoint is absent (older backend → null).
 */
export function CostOfNoPanel() {
  const [days, setDays] = useState(30);
  const query = useQuery({ queryKey: ["cost-of-no", days], queryFn: () => deskApi.costOfNo(days), retry: retryTransient });
  const { data, isLoading, isError } = query;
  if (!isLoading && !isError && data == null) return null;

  const b = data?.blocked;
  const hasRange = b?.ci95 != null;
  const edge = data?.edge_dollars ?? 100;
  const remaining = b?.sessions_remaining;
  const readiness = !b || b.sessions === 0
    ? "No refused runs have been settled in this window yet."
    : !hasRange
      ? "One session only: no range can be computed yet."
      : remaining > 0
        ? `About ${remaining} more sessions are needed before this window could show an edge of ${usdAbs(edge)} a trade either way.`
        : `Enough sessions for a ${usdAbs(edge)}-a-trade edge to show; the range below is the evidence, not the sign.`;

  return (
    <Panel title="Cost of saying no" icon="Scale" data-testid="cost-of-no"
      meta={data?.window ? <span className="sn-num">{data.window.start} → {data.window.end} · {data.window.sessions} sessions</span> : null}
      actions={<SegmentedControl label="Window" value={days} onChange={setDays} size="sm"
        options={[{ value: 30, label: "30 days", testId: "cost-of-no-30" }, { value: 90, label: "90 days", testId: "cost-of-no-90" }]} />}>
      {isLoading ? <Skeleton lines={3} /> : isError && !data ? (
        <QueryError query={query} title="Cost of saying no could not load" what="the refusal scoreboard" />
      ) : (
        <div className="sn-stack">
          <p style={{ margin: 0 }}>
            What the top-ranked spread would have made if it had been traded at every run the system refused, settled at the close.
            A record of the system's refusals, not evidence for or against trading: see the range and the sessions still needed.
          </p>
          <p className="sn-row" style={{ margin: 0, gap: "var(--space-2)" }} data-testid="cost-of-no-sample-note">
            <Badge icon="FlaskConical" className={remaining > 0 || !hasRange ? "sn-prov" : undefined}>{b?.sessions ?? 0} sessions · {b?.n ?? 0} runs</Badge>
            <span style={{ fontWeight: 600 }}>{readiness}</span>
          </p>
          <div className="sn-row" style={{ gap: "var(--space-6)" }} data-testid="cost-of-no-summary">
            <StatCell size="md" label="Average per refused trade" value={b?.per_trade} kind="usd" signed
              sub={hasRange ? `± ${usdAbs(b.ci95)} (95% range across ${b.sessions} sessions)` : "no range yet"} data-testid="cost-of-no-net" />
            <StatCell size="sm" label="Would have won" value={String(b?.winners ?? 0)} sub={`missed ${usd(b?.missed_pnl)}`} />
            <StatCell size="sm" label="Would have lost" value={String(b?.losers ?? 0)} sub={`avoided ${usd(b?.avoided_pnl)}`} />
            <StatCell size="sm" label="Net over the window" value={b?.net} kind="usd" signed sub="positive = refusals cost money" />
            <StatCell size="sm" label="Entered" value={String(data?.entered?.n ?? 0)} sub={`P&L ${usd(data?.entered?.pnl)}`} />
          </div>
          {(data?.groups || []).length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="sn-table" data-testid="cost-of-no-groups" aria-label="Why the system said no">
                <thead><tr><th>Why the system said no</th><th className="r">Runs</th><th className="r">Sessions</th><th className="r">Average per trade</th><th className="r">± range</th><th className="r">Net</th></tr></thead>
                <tbody>
                  {data.groups.map((g) => (
                    <tr key={g.group}>
                      <td>{g.label}</td>
                      <td className="num">{g.n}</td>
                      <td className="num">{g.sessions}</td>
                      <td className="num">{usd(g.per_trade)}</td>
                      <td className="num">{g.ci95 != null ? usdAbs(g.ci95) : "—"}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{usd(g.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(data?.by_rule || []).length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table className="sn-table" data-testid="cost-of-no-by-rule" aria-label="Cost of saying no by reason code">
                <thead><tr><th>Reason</th><th className="r">Runs</th><th className="r">Would have won</th><th className="r">Would have lost</th><th className="r">Net</th></tr></thead>
                <tbody>
                  {data.by_rule.map((r) => (
                    <tr key={r.rule}>
                      <td><span className="id">{r.rule}</span>{r.meaning ? <span className="sn-faint"> · {r.meaning}</span> : null}</td>
                      <td className="num">{r.n}</td>
                      <td className="num">{r.winners}</td>
                      <td className="num">{r.losers}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{usd(r.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="sn-small sn-faint" style={{ margin: 0 }}>
            {data?.unit_note ? `${data.unit_note} ` : ""}{data?.basis ? `Basis: ${data.basis}.` : ""} The decision to keep ENTER off rests on the full backtest (2,349 trades at real fills), not on this window.
          </p>
        </div>
      )}
    </Panel>
  );
}
